// src/utils/sourceIntakeWriters.js
//
// Pass 64 — production-safe, idempotent, actor-authorized source-intake writers. The intake collection
// holds normalized SourceIntakeEnvelopes (customer correspondence) and is org-scoped + operator-gated
// exactly like booking-case writes (owner/admin/ops; instructor/student/cross-org denied; missing actor
// fails closed). Conversion REUSES the canonical createBookingCase writer — no parallel case logic.

import { writeBatch, runTransaction, doc, serverTimestamp } from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from './platformSchema';
import { assertCanWriteBookingCases } from './operationalWriteGate';
import { createBookingCase } from './bookingCaseWriters';
import { buildBookingCaseDraftFromIntake, intakeIdentityEvidence, INTAKE_STATE } from './sourceIntake';
import { resolveClientIdentity, newLeadClientId, RESOLUTION_STATUS } from './clientIdentity';

const INTAKE = PLATFORM_COLLECTIONS.SOURCE_INTAKE;
const authCtx = (ctx = {}) => ({ userData: ctx.userData || null, organizationId: ctx.organizationId || '', action: 'source_intake' });

let _seq = 0;
// A generated stable intake id for sources WITHOUT an external id (manual/paste). Assigned ONCE at
// creation; a re-open of the same operator draft passes the same generatedIntakeId so it stays one doc.
export function newIntakeId() { return `intake_gen_${Date.now().toString(36)}_${(_seq++).toString(36)}`; }

// ── create / upsert one intake envelope (idempotent by deterministic intakeId) ───
// A source with a stable external id → same intakeId → re-ingestion is a no-op (no duplicate doc,
// createdAt preserved). Returns { intakeId, created }.
export async function createSourceIntake(db, intake = {}, ctx = {}) {
  assertCanWriteBookingCases(authCtx(ctx));
  const intakeId = intake.intakeId;
  if (!intakeId) { const e = new Error('Intake id is required.'); e.code = 'INTAKE_ID_REQUIRED'; throw e; }
  if (!intake.organizationId) { const e = new Error('Organisation is required.'); e.code = 'ORG_REQUIRED'; throw e; }

  let created = true;
  await runTransaction(db, async (tx) => {
    const ref = doc(db, INTAKE, intakeId);
    const prior = await tx.get(ref);
    if (prior.exists && prior.exists()) { created = false; return; } // §12/§38 same external record → reuse
    const { errors, ...doc0 } = intake;
    tx.set(ref, {
      ...doc0,
      reviewState: intake.reviewState || INTAKE_STATE.NEW,
      linkedBookingCaseId: '',
      createdBy: ctx.actorUserId || '', createdByName: ctx.actorName || '',
      createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
  });
  return { intakeId, created };
}

// ── ignore / dismiss (spam, duplicate, non-booking). Preserves evidence; creates NOTHING. ─
export async function ignoreSourceIntake(db, intake = {}, { reason = '', ...ctx } = {}) {
  assertCanWriteBookingCases(authCtx(ctx));
  const batch = writeBatch(db);
  batch.update(doc(db, INTAKE, intake.intakeId), {
    reviewState: INTAKE_STATE.IGNORED, ignoredReason: String(reason || '').slice(0, 500),
    ignoredByUserId: ctx.actorUserId || '', ignoredAt: serverTimestamp(), updatedAt: serverTimestamp(),
  });
  await batch.commit();
  return { ignored: true };
}

// ── convert intake → canonical BookingCase (stage=inquiry; ZERO bookings) ────────
// Idempotent: an already-linked intake returns its case; a deterministic caseId derived from intakeId
// means double-click / retry / concurrent operators converge on ONE case (createBookingCase is itself
// transaction-idempotent by its audit doc). Client identity resolves through Pass 63 — never re-implemented.
//   ctx.identityIndex   — optional Pass-63 index to resolve against (buildClientIdentityIndex output)
//   ctx.selectedClientId — an operator-selected existing stable client (overrides auto-resolution)
export async function convertIntakeToBookingCase(db, intake = {}, ctx = {}) {
  assertCanWriteBookingCases(authCtx(ctx));
  if (intake.reviewState === INTAKE_STATE.IGNORED) { const e = new Error('An ignored intake cannot be converted.'); e.code = 'INTAKE_IGNORED'; throw e; }
  if (intake.linkedBookingCaseId) return { caseId: intake.linkedBookingCaseId, created: false, alreadyLinked: true };

  // §10/§11 identity resolution (fails closed on ambiguity/conflict → operator must resolve first)
  const evidence = intakeIdentityEvidence(intake);
  let clientId = String(ctx.selectedClientId || evidence.clientId || '');
  let identityStatus = clientId ? RESOLUTION_STATUS.MATCHED : RESOLUTION_STATUS.NEW;
  if (!clientId) {
    const res = resolveClientIdentity(evidence, ctx.identityIndex || { byEvidence: new Map() });
    identityStatus = res.status;
    if (res.status === RESOLUTION_STATUS.AMBIGUOUS || res.status === RESOLUTION_STATUS.CONFLICT) {
      const e = new Error('Client identity needs review before this enquiry can be created.');
      e.code = 'INTAKE_IDENTITY_REVIEW'; e.status = res.status; e.candidateIds = res.candidateIds; throw e;
    }
    clientId = res.clientId; // MATCHED → existing stable id; NEW/UNRESOLVED → '' (mint below)
  }

  // deterministic case id from the intake → retry/concurrency converge on ONE case
  const caseId = `case_${String(intake.intakeId).replace(/[^A-Za-z0-9_-]/g, '_')}`;
  if (!clientId) clientId = newLeadClientId(caseId); // genuinely new lead → one stable id, reused by children

  const draft = buildBookingCaseDraftFromIntake(intake, { resolvedClientId: clientId });
  // createBookingCase is idempotent by its own audit doc; stage=inquiry ⇒ 0 canonical bookings materialised.
  const res = await createBookingCase(db, { ...draft, caseId }, {
    actorUserId: ctx.actorUserId || '', actorName: ctx.actorName || '',
    userData: ctx.userData || null, organizationId: ctx.organizationId || intake.organizationId,
  });

  // link the intake to the case (recoverable: deterministic caseId means a retry re-links the SAME case)
  const batch = writeBatch(db);
  batch.update(doc(db, INTAKE, intake.intakeId), {
    reviewState: INTAKE_STATE.CONVERTED, linkedBookingCaseId: caseId,
    convertedByUserId: ctx.actorUserId || '', convertedAt: serverTimestamp(),
    identityStatus, updatedAt: serverTimestamp(),
  });
  await batch.commit();
  return { caseId, created: res.created, clientId, identityStatus };
}

// ── explicitly link a follow-up intake to an EXISTING case (operator choice / trusted conversation id) ─
// Never auto-merges on same email/name/dates — this is an explicit action. Appends source provenance.
export async function linkIntakeToCase(db, intake = {}, caseId = '', ctx = {}) {
  assertCanWriteBookingCases(authCtx(ctx));
  if (!caseId) { const e = new Error('A target case id is required.'); e.code = 'CASE_ID_REQUIRED'; throw e; }
  const batch = writeBatch(db);
  batch.update(doc(db, INTAKE, intake.intakeId), {
    reviewState: INTAKE_STATE.CONVERTED, linkedBookingCaseId: caseId, linkedExisting: true,
    linkedByUserId: ctx.actorUserId || '', linkedAt: serverTimestamp(), updatedAt: serverTimestamp(),
  });
  await batch.commit();
  return { caseId, linked: true };
}
